import { isOrganizationSuspended } from '@/store/base-query';

/**
 * The server's own reason when it refuses a change. The API answers with an
 * envelope (`error.details.errors[].message`, else `error.message`) or a
 * plain `detail`; a bare code such as BAD_REQUEST is not a reason. Falls back
 * to the caller's wording.
 */
export function serverReason(error: unknown, fallback: string): string {
  const data = (error as { data?: unknown } | undefined)?.data;
  if (!data || typeof data !== 'object') return fallback;
  const { error: envelope, detail } = data as {
    error?: unknown;
    detail?: unknown;
  };
  return (
    blockedLine(envelope) ??
    blockedLine(detail) ??
    messageOf(envelope) ??
    messageOf(detail) ??
    fallback
  );
}

/**
 * The refusal of a play the agent's settings block (`agent_settings_blocked`,
 * in the envelope's `details` or in a plain `detail`): its own line, which says
 * where to change the setting, not the first blocked step's reason that the
 * list of errors would give.
 */
function blockedLine(value: unknown): string | null {
  if (!value || typeof value !== 'object') return null;
  const item = value as {
    code?: unknown;
    message?: unknown;
    details?: { code?: unknown };
  };
  if (
    item.code !== 'agent_settings_blocked' &&
    item.details?.code !== 'agent_settings_blocked'
  )
    return null;
  return typeof item.message === 'string' && item.message.trim()
    ? item.message.trim()
    : null;
}

function messageOf(value: unknown): string | null {
  if (typeof value === 'string') return value.trim() || null;
  if (!value || typeof value !== 'object') return null;
  const item = value as {
    details?: { errors?: unknown };
    errors?: unknown;
    message?: unknown;
  };
  const errors = item.details?.errors ?? item.errors;
  if (Array.isArray(errors)) {
    for (const entry of errors) {
      const message = (entry as { message?: unknown } | null)?.message;
      if (typeof message === 'string' && message.trim()) return message.trim();
    }
  }
  if (
    typeof item.message === 'string' &&
    item.message.trim() &&
    !/^[A-Z0-9_]+$/.test(item.message.trim())
  )
    return item.message.trim();
  return null;
}

/** The codes of the server's refusal (`calendar_not_connected`), from the
 * same envelope or plain `detail` as `serverReason`. */
export function serverCodes(error: unknown): string[] {
  const data = (error as { data?: unknown } | undefined)?.data;
  if (!data || typeof data !== 'object') return [];
  const { error: envelope, detail } = data as {
    error?: unknown;
    detail?: unknown;
  };
  return [envelope, detail].flatMap((value) => {
    if (!value || typeof value !== 'object') return [];
    const item = value as { details?: { errors?: unknown }; errors?: unknown };
    const errors = item.details?.errors ?? item.errors;
    return Array.isArray(errors)
      ? errors
          .map((entry) => (entry as { code?: unknown } | null)?.code)
          .filter((code): code is string => typeof code === 'string')
      : [];
  });
}

// A connector the play uses that isn't connected yet, by the server's
// refusal code, and the Integrations link that fixes it.
const CONNECT_LABELS: Record<string, string> = {
  calendar_not_connected: 'Connect Google Calendar',
  sheet_not_connected: 'Connect Google Sheets',
  google_docs_not_connected: 'Connect Google Docs',
  notion_not_connected: 'Connect Notion',
  fireflies_not_connected: 'Connect Fireflies',
  fathom_not_connected: 'Connect Fathom',
  gong_not_connected: 'Connect Gong',
};

/** The Connect link a refusal names (`Connect Notion`), or null when the
 * refusal is not a connector that isn't connected yet. */
export function connectLabel(error: unknown): string | null {
  for (const code of serverCodes(error)) {
    const label = CONNECT_LABELS[code];
    if (label) return label;
  }
  return null;
}

/**
 * A read the link itself dooms: no such record in this organization (404) or
 * an id that isn't one (400). Unlike a failed read, a retry can't load it.
 */
export function isMissingRecord(error: unknown): boolean {
  const status = (error as { status?: unknown } | undefined)?.status;
  return status === 404 || status === 400;
}

/**
 * A refusal because the org's plan doesn't include what was asked (a 403),
 * which opens the upgrade prompt, the way a locked sidebar entry does, not a
 * try-again error. A suspended org's 403 is the health check's to show.
 */
export function isPlanRefusal(error: unknown): boolean {
  const status = (error as { status?: unknown } | undefined)?.status;
  return status === 403 && !isOrganizationSuspended();
}

/**
 * The server's line when a template that starts from a CRM is read with none
 * the org can use (a 409 `crm_required`: no CRM connected, or a plan without
 * CRM integrations), or null for any other refusal. A try-again error would
 * hide what to fix.
 */
export function crmRequiredReason(error: unknown): string | null {
  const data = (error as { data?: unknown } | undefined)?.data;
  if (!data || typeof data !== 'object') return null;
  const { error: envelope, detail } = data as {
    error?: unknown;
    detail?: unknown;
  };
  const required = [envelope, detail].some((value) => {
    if (!value || typeof value !== 'object') return false;
    const item = value as { error?: unknown; details?: { error?: unknown } };
    return (
      item.error === 'crm_required' || item.details?.error === 'crm_required'
    );
  });
  return required
    ? serverReason(
        error,
        'This template starts from a CRM. Connect one to use it.'
      )
    : null;
}
